'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faDiamondTurnRight,
  faEllipsisVertical,
} from '@fortawesome/pro-solid-svg-icons';
import { parseISO, format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { Dropdown } from 'src/components/PulseUI/Dropdown';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { PulseIconButton } from 'src/components/PulseUI/PulseIconButton';
import type { RouteStopResponse } from 'src/api/redux/routes';
import type { StopCardState } from 'src/hooks/routes/useRouteDetailLogic';
import type { VisitedStopActivity } from 'src/redux/slices/routeNavigationSlice';
import { formatApiStopAddress } from 'src/hooks/routes/utils';
import { Badge, Card, CardContent, CardTitle } from 'src/components/Routes/components/ui';
import { ROUTE_MENU_CLASS_NAME, STOP_BADGE_LABELS } from '../../constants';

export interface StopCardProps {
  stop: RouteStopResponse;
  state: StopCardState;
  visitedActivity?: VisitedStopActivity;
  isLast?: boolean;
  onGo: (stop: RouteStopResponse) => void;
  onCancel: (stop: RouteStopResponse) => void;
  onViewProfile: (stop: RouteStopResponse) => void;
  onLogActivity: (stop: RouteStopResponse) => void;
  onOpenInMaps: (stop: RouteStopResponse) => void;
  onRemove: (stop: RouteStopResponse) => void;
}

const BADGE_VARIANTS = {
  nextUp: 'success',
  active: 'info',
  visited: 'default',
} as const;

const StopCard: React.FC<StopCardProps> = ({
  stop,
  state,
  visitedActivity,
  isLast = false,
  onGo,
  onCancel,
  onViewProfile,
  onLogActivity,
  onOpenInMaps,
  onRemove,
}) => {
  const address = formatApiStopAddress(stop);
  const badgeKey = state === 'default' ? null : state;

  return (
    <div
      className={twMerge('relative flex flex-col', badgeKey && 'pt-3')}
      data-testid={`stop-card-${stop.id}`}
    >
      {badgeKey && (
        <div className="absolute left-6 top-3 z-10 -translate-y-1/2">
          <Badge
            variant={BADGE_VARIANTS[badgeKey]}
            data-testid={`stop-card-badge-${stop.id}`}
          >
            {STOP_BADGE_LABELS[badgeKey]}
          </Badge>
        </div>
      )}

      <Card
        className={twMerge(
          state === 'nextUp' && 'border-green-500',
          state === 'active' && 'border-sky-600'
        )}
      >
        <CardContent>
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-base truncate">{stop.name}</CardTitle>
            {state === 'visited' ? (
              <Icon
                icon={faCircleCheck}
                className="text-neutral-500"
                title="Visited"
                data-testid={`stop-visited-icon-${stop.id}`}
              />
            ) : (
              <Dropdown
                position="bottom-right"
                className={ROUTE_MENU_CLASS_NAME}
                ariaLabel={`Stop options for ${stop.name}`}
                options={[
                  ...(state === 'active'
                    ? [{ label: 'Cancel Stop', onClick: () => onCancel(stop) }]
                    : []),
                  {
                    label: 'Open in Maps',
                    onClick: () => onOpenInMaps(stop),
                  },
                  {
                    label: 'Remove from Route',
                    onClick: () => onRemove(stop),
                  },
                ]}
                slots={{
                  trigger: {
                    component: (
                      <PulseIconButton
                        variant="plain"
                        color="neutral"
                        size="sm"
                        icon={faEllipsisVertical}
                        aria-label={`Stop options for ${stop.name}`}
                        data-testid={`stop-menu-${stop.id}`}
                      />
                    ),
                  },
                }}
              />
            )}
          </div>

          {state !== 'default' && address && (
            <div className="flex items-stretch gap-2">
              <div className="w-0.5 self-stretch rounded-full bg-neutral-300" />
              <span className="text-xs font-semibold text-neutral-800 truncate">
                {address}
              </span>
            </div>
          )}

          {state === 'visited' && visitedActivity && (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-neutral-500">
                {visitedActivity.typeLabel} &middot;{' '}
                {format(parseISO(visitedActivity.date), 'MMM d, yyyy')}
              </span>
              {visitedActivity.note && (
                <p className="rounded-xl bg-neutral-100 px-3 py-2 text-sm text-neutral-700 line-clamp-2">
                  {visitedActivity.note}
                </p>
              )}
            </div>
          )}

          {state === 'nextUp' && (
            <PulseButton
              variant="solid"
              color="success"
              className="w-full rounded-full"
              onClick={() => onGo(stop)}
              startDecorator={faDiamondTurnRight}
              data-testid={`stop-go-${stop.id}`}
            >
              Go
            </PulseButton>
          )}

          {state === 'active' && (
            <div className="flex gap-3">
              <PulseButton
                variant="soft"
                color="neutral"
                className="flex-1 rounded-full"
                onClick={() => onViewProfile(stop)}
                data-testid={`stop-view-profile-${stop.id}`}
              >
                View Profile
              </PulseButton>
              <PulseButton
                variant="solid"
                color="primary"
                className="flex-1 rounded-full"
                onClick={() => onLogActivity(stop)}
                data-testid={`stop-log-activity-${stop.id}`}
              >
                Log Activity
              </PulseButton>
            </div>
          )}
        </CardContent>
      </Card>

      {!isLast && (
        <div className="pl-7">
          <div className="w-0.5 h-5 rounded-full bg-neutral-300" />
        </div>
      )}
    </div>
  );
};

export default StopCard;
